import { HbrOption, HbrSelect, HbrTextarea } from "@harbor/elements-react-standalone";
import React, { useEffect, useMemo } from "react";
import {
  ApprovalRequest,
  ApprovalRequestPayload,
  ApprovalRequestSource,
  ApprovalRequestStatusLevel,
  EscalationLevel,
  EscalationRequestAssignmentPayload,
  RequestHistoryPayload
} from "../../types/approval-request";
import StbApiModal from "../security-toolbox-ui/StbApiModal";
import { useForm, Controller } from "react-hook-form"
import { renderErrorMessage } from "../security-toolbox-ui/security-toolbox-ui-utils";
import { postItem } from "../../support/queries";
import { useCreateApprovalHistoryMutation, useUpdateApprovalRequestMutation } from "../../hooks/approval-request-hooks";
import { useFetchEscalationRequest, useUpdateEscalationMutation } from "../../hooks/escalation-request-hooks";
import { useSelector } from "react-redux";
import { User } from "../../types/user";


type FormValues = {
  escalationLevel: string,
  specialist: string | null,
  escalationType: string | null,
  escalationDetails: string,
  approverNote: string
};

const initialValues: FormValues = {
  escalationLevel: String(EscalationLevel.Domain),
  specialist: null,
  escalationType: '',
  escalationDetails: '',
  approverNote: ''
};

const EscalationAssignmentModal: React.FC<{
  request: ApprovalRequest,
  open: boolean,
  closeModal: () => Promise<void>
}> = ({
  request,
  open,
  closeModal
}) => {
  const { id: userId } = useSelector((state: { user: { userData: User } }) => state.user.userData);
  const { control, handleSubmit, reset, formState, watch } = useForm<FormValues>({
    defaultValues: initialValues,
    mode: 'onChange',
  });
  const selectedEscalationLevel = watch('escalationLevel');
  const selectedSpecialist = watch('specialist');
  const opportunityId = request.requested_item?.[ApprovalRequestSource.Opportunity] ?? '';

  const { mutateAsync: updateApprovalRequest } = useUpdateApprovalRequestMutation(opportunityId);
  const { mutateAsync: createApprovalHistory } = useCreateApprovalHistoryMutation();
  const { mutateAsync: updateEscalation } = useUpdateEscalationMutation(opportunityId);
  const { data: escalation, isLoading: loadingEscalation } = useFetchEscalationRequest(
    opportunityId,
    request.requested_item?.escalation ?? '',
    {},
    { enabled: request?.requested_item?.escalation !== undefined }
  );
  const escalationTypes = useMemo(() => {
    if (!escalation || loadingEscalation) return [];
    const buPath = escalation?.available_support_paths?.find(path => path.assignment_type_level === EscalationLevel.Business);
    if (!buPath) return [];

    return (buPath?.bu_request_mappings ?? [])
      .map(mapping => ({
        id: mapping.mapping_id,
        name: mapping.request_type_name
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [escalation, loadingEscalation]);

  const optionData = useMemo(() =>(escalation?.available_support_paths ?? []).map((path) => ({
    id: path.assignment_type_id,
    name: path.assignment_type_name,
    level: path.assignment_type_level,
    members: path.group_members
  })), [escalation]);

  const escalationLevelOptions = useMemo(() => {
    if (loadingEscalation) return [];
    return optionData.filter((option) => option.level >= (escalation?.stage ?? 0)).sort((a, b) => a.level - b.level);
  }, [optionData, escalation, loadingEscalation]);

  const specialistOptions = useMemo(() => optionData.find(option => option.level === Number(selectedEscalationLevel))?.members || [], [optionData, selectedEscalationLevel]);

  const seEscalation = Number(selectedEscalationLevel) === EscalationLevel.Domain;
  const specialistEscalation = Number(selectedEscalationLevel) === EscalationLevel.Incubation && escalation?.stage === EscalationLevel.Incubation;
  const businessUnitEscalation = Number(selectedEscalationLevel) === EscalationLevel.Business;
  const escalationStatus = seEscalation
    ? ApprovalRequestStatusLevel.Approved
    : specialistEscalation
      ? ApprovalRequestStatusLevel.ReEscalated
      : ApprovalRequestStatusLevel.Superseded;
  const approvalStatus = seEscalation || specialistEscalation
    ? ApprovalRequestStatusLevel.Approved
    : ApprovalRequestStatusLevel.Superseded;

  const defaultEscalationType = useMemo(() => {
    return escalationTypes?.[0]?.id ?? null;
  }, [escalationTypes]);

  useEffect(() => {
    if (open && escalationLevelOptions.length > 0) {
      const values = {
        ...initialValues,
        escalationLevel: String(escalationLevelOptions[0].level),
        escalationType: String(defaultEscalationType),
        escalationDetails: request?.justification || '',
        approverNote: ''
      };
      reset(values);
    }
  }, [open, escalationLevelOptions, reset, request, defaultEscalationType]);

  const onClose = async () => {
    reset(initialValues);
    closeModal();
  }

  const onSubmit = async (data: FormValues) => {
    if (!request || !escalation || loadingEscalation) return;
    const supportPath = escalation?.available_support_paths?.find(p => p.assignment_type_level === Number(data.escalationLevel));
    if (!supportPath) return;

    const escalationAssignmentPayload: EscalationRequestAssignmentPayload = {
      escalation_id: escalation?.id,
      assignment_type_id: Number(data.escalationLevel),
      status_id: seEscalation ? ApprovalRequestStatusLevel.Completed : ApprovalRequestStatusLevel.Unassigned,
      assigned_to_id: seEscalation ? Number(data.specialist) : null,
      product_support_id: supportPath.id,
      requested_by_id: seEscalation || specialistEscalation ? request.requestor.id : userId,
      ...(businessUnitEscalation && { 
        is_external: true,
        external_system: 'jira',
        mapping_id: Number(data.escalationType)
      })
    }

    const escalationRequestUpdate = {
      id: escalation?.id,
      description: data.escalationDetails ?? '',
      status_id: escalationStatus,
    }

    const approvalRequestPayload: Omit<ApprovalRequestPayload, 'requested_item_id'> = {
      id: request?.id,
      requestor_id: request?.requestor?.id,
      approver_id: request?.approver?.id,
      requested_item_type: request?.requested_item_type,
      approver_note: data.approverNote ?? '',
      status_id: approvalStatus
    };

    const approvalRequestHistory: RequestHistoryPayload = {
      approval_request_id: request?.id,
      previous_status_id: request?.status.id ?? null,
      current_status_id: approvalStatus,
      current_approver_note: data.approverNote ?? '',
      previous_approver_note: request?.approver_note ?? null,
      action_by_user_id: userId,
    }

    await Promise.all([
      postItem('db/escalation-assignment', escalationAssignmentPayload),
      updateEscalation(escalationRequestUpdate),
      updateApprovalRequest(approvalRequestPayload),
      createApprovalHistory(approvalRequestHistory)
    ]);
  }

  return (
    <StbApiModal
      open={open}
      title="Assign Escalation Request"
      role="save"
      submitLabel="Assign Request"
      submitDisabled={
        (Number(selectedEscalationLevel) === EscalationLevel.Domain && !selectedSpecialist) ||
        Object.keys(formState.errors).length > 0
      }
      submitSuccess="Request assigned successfully"
      onClose={onClose}
      onSubmit={handleSubmit(onSubmit)}
    >
      <form>
        <Controller
          name="escalationLevel"
          control={control}
          rules={{
            validate: (value) => value ? true : 'Resource type is required'
          }}
          render={({ field }) => {
            const options = escalationLevelOptions;
            return (
              <HbrSelect
                label="Resource Type"
                value={field.value}
                disabled={!request || escalationLevelOptions.length <= 1}
                onHbr-change={e => field.onChange(e.target.value)}
              >
                {options.map((option) => (
                  <HbrOption key={option.id} value={String(option.level)}>
                    {option.name}
                  </HbrOption>
                ))}
              </HbrSelect>
            )
          }}
        />

        {Number(selectedEscalationLevel) === EscalationLevel.Domain && (
          <Controller
            name="specialist"
            control={control}
            rules={{
              validate: (value) => value ? true : 'Domain specialist is required'
            }}
            render={({ field }) => {
              const options = specialistOptions;
              return (
                <HbrSelect
                  value={field.value}
                  disabled={!request}
                  onHbr-change={e => field.onChange(e.target.value)}
                  placeholder="Select a specialist..."
                >
                  <div slot="label">Domain Specialist <em className="text-gray-500"> (required)</em></div>
                  {options.map((option) => (
                    <HbrOption key={option.id} value={String(option.id)}>
                      {option.email}
                    </HbrOption>
                  ))}
                </HbrSelect>
              )
            }}
          />
        )}

        {Number(selectedEscalationLevel) === EscalationLevel.Business && (
          <Controller
            name="escalationType"
            control={control}
            rules={{
              validate: (value) => value ? true : 'Escalation type is required'
            }}
            render={({ field }) => {
              const options = escalationTypes ?? [];
              return (
                <HbrSelect
                  value={field.value}
                  disabled={!escalationTypes || escalationTypes.length === 0}
                  onHbr-change={e => field.onChange(e.target.value)}
                  placeholder="Select an escalation type..."
                >
                  <div slot="label">Escalation Type</div>
                  {options.map((option) => (
                    <HbrOption key={option.id} value={String(option.id)}>
                      {option.name}
                    </HbrOption>
                  ))}
                </HbrSelect>
              )
            }}
          />
        )}

        <Controller
          name="escalationDetails"
          control={control}
          rules={{
            validate: (value) => value && value.trim() !== '' ? true : 'Escalation details are required'
          }}
          render={({ field }) => (
            <HbrTextarea
              value={field.value}
              disabled={true}
              onHbr-input={e => field.onChange(e.target.value)}
              rows={3}
              error={!!formState.errors['escalationDetails']}
            >
              <div slot="label">Escalation Details</div>
              {renderErrorMessage(!!formState.errors['escalationDetails'], formState.errors['escalationDetails']?.message as string)}
            </HbrTextarea>
          )}
        />

        <Controller
          name="approverNote"
          control={control}
          render={({ field }) => (
            <HbrTextarea
              label="Approver Note"
              value={field.value}
              onHbr-input={e => field.onChange(e.target.value)}
              rows={3}
            />
          )}
        />
      </form>
    </StbApiModal>
  )
}

export default EscalationAssignmentModal;